import { endpoints } from '../../api';
import { ApiError } from '../../api/client';
import type { CreateUserVoice } from '../../api/user-voices';

export interface CloneIntent {
  version: 1;
  kind: 'create' | 'retry';
  key: string;
  body: CreateUserVoice | { name?: string; description?: string; denoise?: boolean };
  id?: number;
  expectedVersion?: number;
}
const inputRejections = new Set(['voice_name_invalid', 'voice_description_invalid', 'voice_invalid',
  'samples_invalid', 'sample_invalid', 'sample_duration_invalid', 'consent_invalid']);

/** Namespace comes from the authenticated server session, never from an unverified Telegram payload. */
export async function intentSlot(kind: 'create' | 'retry', id?: number) {
  const { me } = await endpoints.session();
  const owner = me.user;
  if (!Number.isSafeInteger(owner.id) || owner.id <= 0 || !Number.isSafeInteger(owner.telegram_user_id) || owner.telegram_user_id <= 0)
    throw new Error('clone_intent_owner_unavailable');
  return `steos:clone:intent:v1:${owner.id}:${owner.telegram_user_id}:${kind}:${id ?? ''}`;
}

export function readIntent(slot: string): CloneIntent | null {
  const raw = sessionStorage.getItem(slot);
  if (!raw) return null;
  const value = JSON.parse(raw) as CloneIntent;
  if (value.version !== 1 || !['create', 'retry'].includes(value.kind) ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.key) ||
      !value.body || typeof value.body !== 'object' || Array.isArray(value.body))
    throw new Error('clone_intent_invalid');
  return value;
}

/** Persist before the first POST; storage failure prevents an unresumable mutation. */
export async function submitIntent<T>(slot: string, proposed: CloneIntent, send: (intent: CloneIntent) => Promise<T>): Promise<T> {
  const intent = readIntent(slot) ?? proposed;
  sessionStorage.setItem(slot, JSON.stringify(intent));
  try {
    const result = await send(intent);
    sessionStorage.removeItem(slot);
    return result;
  } catch (failure) {
    if (failure instanceof ApiError && (failure.status === 422 && inputRejections.has(failure.code) ||
        intent.kind === 'retry' && failure.status === 409 && failure.code === 'version_conflict')) sessionStorage.removeItem(slot);
    throw failure;
  }
}
